import { getSettingsMessage, SettingsMessageKey } from '../constants/pageMessages';
import {
  ApprovalRequestData,
  ApprovalRequestPartyData,
} from '../dto/EntityRepository';
import {
  ApprovalRequestStatus,
  ApprovalRequestType,
} from '../enum/EnumRepository';

export const isIncomingApprovalRequest = (
  request: ApprovalRequestData,
  accountAui: string,
): boolean => request.approver.aui === accountAui;

const getApprovalRequestPartyName = (
  party: ApprovalRequestPartyData,
): string => party.name.trim() || party.handle.trim() || party.aui;

const getApprovalRequestSubject = (
  request: ApprovalRequestData,
): string => {
  switch (request.type) {
    case ApprovalRequestType.GROUP_MEMBER_CONNECTION:
      return getSettingsMessage('GROUP_MEMBER_CONNECTION_REQUEST_SUBJECT');
    case ApprovalRequestType.WORK_USAGE:
      return getSettingsMessage('WORK_USAGE_REQUEST_SUBJECT', {
        title: request.detail.workTitle,
      });
    case ApprovalRequestType.TEXT_COPY:
      return getSettingsMessage('TEXT_COPY_REQUEST_SUBJECT', {
        count: request.detail.texts.length,
      });
    case ApprovalRequestType.WORK_COPY:
      return getSettingsMessage('WORK_COPY_REQUEST_SUBJECT', {
        count: request.detail.works.length,
      });
    default: {
      const _exhaustive: never = request;
      return _exhaustive;
    }
  }
};

const getGroupMemberIncomingMessageKey = (
  status: ApprovalRequestStatus,
): SettingsMessageKey => {
  switch (status) {
    case ApprovalRequestStatus.PENDING:
      return 'GROUP_MEMBER_CONNECTION_INCOMING_PENDING';
    case ApprovalRequestStatus.ACCEPTED:
      return 'GROUP_MEMBER_CONNECTION_INCOMING_ACCEPTED';
    case ApprovalRequestStatus.REJECTED:
      return 'GROUP_MEMBER_CONNECTION_INCOMING_REJECTED';
    case ApprovalRequestStatus.CANCELED:
      return 'GROUP_MEMBER_CONNECTION_INCOMING_CANCELED';
    case ApprovalRequestStatus.EXPIRED:
      return 'GROUP_MEMBER_CONNECTION_INCOMING_EXPIRED';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

const getApprovalRequestMessageKey = (
  status: ApprovalRequestStatus,
  isIncoming: boolean,
): SettingsMessageKey => {
  switch (status) {
    case ApprovalRequestStatus.PENDING:
      return isIncoming
        ? 'APPROVAL_REQUEST_INCOMING_PENDING'
        : 'APPROVAL_REQUEST_OUTGOING_PENDING';
    case ApprovalRequestStatus.ACCEPTED:
      return isIncoming
        ? 'APPROVAL_REQUEST_INCOMING_ACCEPTED'
        : 'APPROVAL_REQUEST_OUTGOING_ACCEPTED';
    case ApprovalRequestStatus.REJECTED:
      return isIncoming
        ? 'APPROVAL_REQUEST_INCOMING_REJECTED'
        : 'APPROVAL_REQUEST_OUTGOING_REJECTED';
    case ApprovalRequestStatus.CANCELED:
      return isIncoming
        ? 'APPROVAL_REQUEST_INCOMING_CANCELED'
        : 'APPROVAL_REQUEST_OUTGOING_CANCELED';
    case ApprovalRequestStatus.EXPIRED:
      return isIncoming
        ? 'APPROVAL_REQUEST_INCOMING_EXPIRED'
        : 'APPROVAL_REQUEST_OUTGOING_EXPIRED';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

export const getApprovalRequestMessage = (
  request: ApprovalRequestData,
  accountAui: string,
): string => {
  const isIncoming = isIncomingApprovalRequest(request, accountAui);
  const otherParty = isIncoming ? request.requester : request.approver;
  const name = getApprovalRequestPartyName(otherParty);

  if (
    request.type === ApprovalRequestType.GROUP_MEMBER_CONNECTION
    && isIncoming
  ) {
    return getSettingsMessage(
      getGroupMemberIncomingMessageKey(request.status),
      { name },
    );
  }

  return getSettingsMessage(
    getApprovalRequestMessageKey(request.status, isIncoming),
    {
      name,
      subject: getApprovalRequestSubject(request),
    },
  );
};
